/**
 * Shared formatting + derivation for the chart-history game ("Call the Chart"),
 * used by the full game page and the homepage teaser so both describe a round identically.
 */

export const ASSET_CLASS_LABELS: Record<string, string> = {
  equity: "Stock",
  forex: "FX",
  crypto: "Crypto",
  futures: "Futures",
};

// One decimal reads best, but a sub-0.05% move rounds to "+0.0%" — which looks like a
// contradiction next to an "up" verdict (the server classifies on the raw sign). Show enough
// decimals for the number to carry its own sign.
export const pct = (v: number): string =>
  `${v > 0 ? "+" : ""}${v.toFixed(Math.abs(v) < 0.05 && v !== 0 ? 3 : 1)}%`;

export const pctOrDash = (v: number | null | undefined): string =>
  typeof v === "number" ? pct(v) : "—";

// FLOOR, not round: a third of the pool scores >= 0.995, which rounds to "a 100% shape match" —
// read as "identical", which it is not. Flooring understates by <1pt and can only print 100 for
// a literally perfect match.
export const similarityPct = (v: number): number => Math.floor(v * 100);

// "an 87%" / "a 96%" — the numbers we print here are read aloud as digits, so only 8x (and 11/18)
// take "an".
export const article = (n: number): string => (/^(8|11$|18$)/.test(String(n)) ? "an" : "a");

/**
 * The archive stores a title-cased ticker as the name when it has no real one ("CSX" → "Csx",
 * "$USDJPY" → "Usd Jpy"), which renders as the ticker stuttered twice. Drop it in that case.
 */
export function displayName(ticker: string, name: string | null | undefined): string | null {
  if (!name) return null;
  const norm = (s: string): string => s.replace(/[^a-z0-9]/gi, "").toUpperCase();
  return norm(name) === norm(ticker) ? null : name;
}

export interface AnalogStats {
  count: number;
  median: number | null;
  p25: number | null;
  p75: number | null;
  worst: number | null;
  best: number | null;
}

/**
 * Dispersion of what actually followed the matched windows, at the headline horizon.
 *
 * This is the honest form of the analog panel: a spread of realized outcomes, with no
 * directional call attached to it (see the Game component's doc comment).
 */
export function analogStats(curve: ForwardCurve | null | undefined): AnalogStats | null {
  if (!curve || curve.curve.length === 0) return null;
  const entry =
    curve.curve.find(c => c.horizon === curve.headline_horizon) ??
    curve.curve[curve.curve.length - 1];
  if (!entry.count) return null;
  const returns = entry.returns;
  return {
    count: entry.count,
    median: entry.median_return,
    p25: entry.p25,
    p75: entry.p75,
    worst: returns.length ? Math.min(...returns) : null,
    best: returns.length ? Math.max(...returns) : null,
  };
}
